import React from "react";

import { Checkbox, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DataGridProps, DataGrid, GridColDef } from "@mui/x-data-grid";

import {
  applyFiltersAndSorting,
  createFilterSortHandler,
  createExternalFilterSortHandlers,
  generateColumnId,
} from "./filterSortUtils";
import { CustomGridFilter } from "./filters/CustomGridFilter";
import updateHierarchicalSelection, {
  cancelPendingUpdates,
  updateParentSelectionStates,
} from "./hierarchicalSelection";
import {
  isRowEffectivelySelected,
  getChildrenSelectionState,
  getHeaderCheckboxState,
  getParentId,
  getAllDescendants,
  RowData,
} from "./selectionUtils";
import { CustomGridRef, SortFilters } from "./types";

const StyledDataGrid = styled(DataGrid)(() => ({
  "--unstable_DataGrid-radius": "0px",

  "& .MuiCheckbox-root": {
    color: "#D0D0CE",
  },

  // Grid structure
  "& .MuiDataGrid-root": {
    borderRadius: "0px",
  },
  "& .MuiDataGrid": {
    borderRadius: "0px",
  },
  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell": {
    borderRadius: "0 !important",
  },
  "& .MuiDataGrid-columnSeparator": {
    display: "none",
  },

  // Rows styling
  "& .MuiDataGrid-row": {
    borderRadius: "0 !important",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: "white",
    },
  },

  // Footer and scrolling
  "& .MuiDataGrid-footerContainer": {
    borderRadius: "0 !important",
    backgroundColor: "white",
  },
  "& .MuiDataGrid-virtualScroller": {
    borderRadius: "0 !important",
    overflowY: "auto !important",
  },
}));

// Helper functions moved outside component to reduce nesting
const createInteractiveHeaderRenderer = (
  column: GridColDef,
  data: string[],
  currentFilterState: any,
  columnId: string,
  createFilterCallback: (
    _columnId: string
  ) => (_config: { sort: string; filters: string[] }) => void
) => {
  return () => (
    <CustomGridFilter
      columnName={column.headerName || column.field}
      sortable={column.sortable ?? false}
      filterable={column.filterable ?? false}
      data={data}
      initialSort={currentFilterState?.sort || ""}
      initialFilters={currentFilterState?.filters || []}
      callbackApplyFilters={createFilterCallback(columnId)}
    />
  );
};

const createSimpleHeaderRenderer = (column: GridColDef) => {
  return () => <Typography fontWeight="bold">{column.headerName || column.field}</Typography>;
};

interface CustomGridProps extends DataGridProps {
  allRows?: RowData[];
  onSelectionChange?: (_selectedRows: any[]) => void;
  initialSelectedRowIds?: string[];
  initialExplicitlyDeselectedIds?: string[];
  // Additional props can be added here later
}

// Internal component without forwardRef
const CustomGridInternal: React.FC<
  CustomGridProps & { forwardedRef?: React.Ref<CustomGridRef> }
> = ({
  allRows,
  onSelectionChange,
  initialSelectedRowIds,
  initialExplicitlyDeselectedIds,
  columns,
  rows,
  checkboxSelection = false,
  keepNonExistentRowsSelected = false,
  forwardedRef,
  ...restProps
}) => {
  // Maintain selection state independently of rows
  const [selectedRowIds, setSelectedRowIds] = React.useState<Set<any>>(
    new Set(initialSelectedRowIds || [])
  );
  const [explicitlyDeselectedIds, setExplicitlyDeselectedIds] = React.useState<Set<any>>(
    new Set(initialExplicitlyDeselectedIds || [])
  );

  // State for sorting and filtering
  const [sortFilters, setSortFilters] = React.useState<SortFilters>({});

  // Track if we're updating from external props to avoid notification loops
  const isUpdatingFromPropsRef = React.useRef(false);

  // Sync external initialSelectedRowIds with internal state
  React.useEffect(() => {
    if (initialSelectedRowIds && !isUpdatingFromPropsRef.current) {
      // Check if the external selection is actually different from our internal state
      const currentInternalIds = new Set(Array.from(selectedRowIds));
      const newExternalIds = new Set(initialSelectedRowIds);

      // Compare sets - only update if they're actually different
      const hasChanged =
        currentInternalIds.size !== newExternalIds.size ||
        ![...currentInternalIds].every((id) => newExternalIds.has(id));

      if (hasChanged) {
        isUpdatingFromPropsRef.current = true;

        // Cancel any pending hierarchical updates when selection changes externally
        cancelPendingUpdates();

        // Filter to keep only valid IDs that exist in current allRows
        const validIds = initialSelectedRowIds.filter(
          (id) => allRows?.some((row) => row.id === id) || false
        );

        setSelectedRowIds(new Set(validIds));
        // Reset the flag after state update is complete
        globalThis.setTimeout(() => {
          isUpdatingFromPropsRef.current = false;
        }, 0);
      }
    }
  }, [initialSelectedRowIds, allRows, selectedRowIds]);

  // Sync external initialExplicitlyDeselectedIds with internal state
  React.useEffect(() => {
    if (initialExplicitlyDeselectedIds !== undefined) {
      setExplicitlyDeselectedIds(new Set(initialExplicitlyDeselectedIds));
    }
  }, [initialExplicitlyDeselectedIds, rows]);

  // Cancel pending hierarchical updates when rows change (navigation)
  React.useEffect(() => {
    cancelPendingUpdates();
  }, [rows]);

  // Cleanup pending updates on unmount
  React.useEffect(() => {
    return () => {
      cancelPendingUpdates();
    };
  }, []);

  // Helper functions using utilities
  const isRowSelected = React.useCallback(
    (rowId: any): boolean => {
      return isRowEffectivelySelected(
        rowId,
        selectedRowIds,
        explicitlyDeselectedIds,
        allRows || []
      );
    },
    [selectedRowIds, explicitlyDeselectedIds, allRows]
  );

  const getChildrenState = React.useCallback(
    (parentId: any) => {
      return getChildrenSelectionState(
        parentId,
        allRows || [],
        selectedRowIds,
        explicitlyDeselectedIds
      );
    },
    [allRows, selectedRowIds, explicitlyDeselectedIds]
  );

  // Create handler for filter and sort updates using utility function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleApplyFilters = React.useCallback(createFilterSortHandler(setSortFilters), [
    setSortFilters,
  ]);

  // Create external handlers for programmatic filter/sort management
  const externalHandlers = React.useMemo(
    () => createExternalFilterSortHandlers(setSortFilters),
    [setSortFilters]
  );

  // Calculate header checkbox state based on all visible rows
  const getHeaderState = React.useCallback(() => {
    return getHeaderCheckboxState(
      (rows as RowData[]) || [],
      selectedRowIds,
      explicitlyDeselectedIds,
      allRows || []
    );
  }, [rows, selectedRowIds, explicitlyDeselectedIds, allRows]);

  //Add selected field to rows data based on selection state and apply filters/sorting
  const processedRows = React.useMemo(() => {
    if (!rows) return [];

    // Convert readonly array to mutable array and apply filters/sorting
    const mutableRows = Array.from(rows);
    const filteredAndSortedRows = applyFiltersAndSorting(mutableRows, sortFilters, columns || []);

    // Then add selection state
    return filteredAndSortedRows.map((row) => ({
      ...row,
      selected: isRowSelected(row.id),
    }));
  }, [rows, isRowSelected, sortFilters, columns]);
  // Track selection updates to detect stale setTimeout callbacks
  const selectionUpdateCountRef = React.useRef<number>(0);

  // Function to update row selection with hierarchical logic
  const updateRowSelection = React.useCallback(
    (rowId: any, selected: boolean) => {
      if (!allRows) return;

      // Increment counter for this operation
      const operationId = ++selectionUpdateCountRef.current;
      let callCount = 0;

      const handleSelectionUpdate = (newSelectedIds: Set<any>, newDeselectedIds: Set<any>) => {
        callCount++;

        // CRITICAL FIX: Prevent clearing selection when we're selecting something
        // The hierarchicalSelection calls handleSelectionUpdate twice:
        // 1st call (immediate): valid update with correct IDs
        // 2nd call (setTimeout): can be stale with empty set due to closure
        // Only block if:
        // 1. This is the 2nd+ call (callCount > 1)
        // 2. We're selecting (selected=true)
        // 3. The update gives us an empty set
        // 4. This operation is still current (not superseded)
        const isStaleSecondCall =
          callCount > 1 &&
          selected &&
          newSelectedIds.size === 0 &&
          operationId === selectionUpdateCountRef.current;

        if (isStaleSecondCall) {
          return;
        } // Set flag to prevent external sync from interfering
        isUpdatingFromPropsRef.current = true;

        setSelectedRowIds(newSelectedIds);
        setExplicitlyDeselectedIds(newDeselectedIds);

        // Reset the flag after a much longer delay to ensure all external effects complete
        globalThis.setTimeout(() => {
          isUpdatingFromPropsRef.current = false;
        }, 500);
      };

      updateHierarchicalSelection(
        rowId,
        selected,
        allRows,
        selectedRowIds,
        explicitlyDeselectedIds,
        handleSelectionUpdate
      );
    },
    [allRows, selectedRowIds, explicitlyDeselectedIds]
  );

  // Function to handle select all functionality
  const handleSelectAll = React.useCallback(
    (selected: boolean) => {
      if (!rows || !allRows) return;

      const rowsArray = rows as RowData[];

      // Get all root level rows (those without parents in the visible rows)
      const rootRows = rowsArray.filter((row) => {
        const parentId = getParentId(row);
        return !rowsArray.some((parentRow) => parentRow.id === parentId);
      });

      // Cancel any pending hierarchical updates
      cancelPendingUpdates();

      // Set flag to prevent external sync from interfering
      isUpdatingFromPropsRef.current = true;

      // Create new sets for selection state
      let newSelectedIds = new Set(selectedRowIds);
      let newDeselectedIds = new Set(explicitlyDeselectedIds);

      if (selected) {
        // Select all root rows and their descendants
        for (const row of rootRows) {
          newSelectedIds.add(row.id);
          newDeselectedIds.delete(row.id);

          // Also select all descendants using the utility function
          const descendants = getAllDescendants(row.id, allRows);
          for (const id of descendants) {
            newSelectedIds.add(id);
            newDeselectedIds.delete(id);
          }
        }
      } else {
        // Deselect all root rows and their descendants
        for (const row of rootRows) {
          newSelectedIds.delete(row.id);

          // Also deselect all descendants using the utility function
          const descendants = getAllDescendants(row.id, allRows);
          for (const id of descendants) {
            newSelectedIds.delete(id);
            newDeselectedIds.delete(id);
          }
        }
      }

      // Update the state
      setSelectedRowIds(newSelectedIds);
      setExplicitlyDeselectedIds(newDeselectedIds);

      // After updating selection, update parent states based on hierarchical logic
      // This ensures that when all children are selected, parents are automatically selected too
      const { newSelectedSet, newDeselectedSet } = updateParentSelectionStates(
        allRows,
        newSelectedIds,
        newDeselectedIds
      );

      // Update with the hierarchically corrected states
      if (
        newSelectedSet.size !== newSelectedIds.size ||
        newDeselectedSet.size !== newDeselectedIds.size
      ) {
        setSelectedRowIds(newSelectedSet);
        setExplicitlyDeselectedIds(newDeselectedSet);
      }

      // Reset the flag after state update
      globalThis.setTimeout(() => {
        isUpdatingFromPropsRef.current = false;
      }, 500);
    },
    [rows, allRows, selectedRowIds, explicitlyDeselectedIds]
  );

  // Get selected rows - include both visible and non-visible selected rows when keepNonExistentRowsSelected is true
  const selectedRows = React.useMemo(() => {
    const visibleSelectedRows = processedRows.filter((row) => {
      const isSelected = isRowSelected(row.id);

      return isSelected;
    });

    if (keepNonExistentRowsSelected && allRows) {
      // Also include selected rows that are not in current view but exist in allRows
      const visibleRowIds = new Set(processedRows.map((row) => row.id));
      const nonVisibleSelectedRows = allRows.filter((row) => {
        const isSelected = isRowSelected(row.id);
        const isVisible = visibleRowIds.has(row.id);

        return isSelected && !isVisible;
      });

      const totalSelected = [...visibleSelectedRows, ...nonVisibleSelectedRows];
      return totalSelected;
    }

    return visibleSelectedRows;
  }, [
    processedRows,
    isRowSelected,
    keepNonExistentRowsSelected,
    allRows,
    selectedRowIds,
    explicitlyDeselectedIds,
  ]);

  // Notify parent component when selection changes - use ref to prevent unnecessary calls
  const previousSelectedRowsRef = React.useRef<RowData[]>([]);
  React.useEffect(() => {
    if (onSelectionChange) {
      // Only call onSelectionChange if selection has actually changed - use Set comparison for IDs
      const currentIds = new Set(selectedRows.map((row) => row.id));
      const previousIds = new Set(previousSelectedRowsRef.current.map((row) => row.id));

      // Check if sets are equal (same size and all elements match)
      const hasChanged =
        currentIds.size !== previousIds.size || ![...currentIds].every((id) => previousIds.has(id));

      if (hasChanged) {
        previousSelectedRowsRef.current = selectedRows;

        // Always notify parent of selection changes, even during hierarchical updates
        // The parent needs to know about hierarchical selections to update its own state
        onSelectionChange(selectedRows);
      }
    }
  }, [selectedRows, onSelectionChange]);

  // Expose methods through ref
  React.useImperativeHandle(
    forwardedRef,
    () => ({
      selectedRows,
      updateRowSelection,
      sortFilters,
      applyColumnSortFilter: externalHandlers.applyColumnSortFilter,
      clearAllSortFilters: externalHandlers.clearAllSortFilters,
      clearColumnSortFilter: externalHandlers.clearColumnSortFilter,
    }),
    [selectedRows, updateRowSelection, sortFilters, externalHandlers]
  );

  // Helper function to create callback for applying filters
  const createFilterCallback = React.useCallback(
    (columnId: string) => (config: { sort: string; filters: string[] }) => {
      handleApplyFilters(columnId, config.sort, config.filters);
    },
    [handleApplyFilters]
  );

  // Helper function to calculate filtered data for a column, excluding its own filter
  const calculateFilteredDataForColumn = React.useCallback(
    (column: GridColDef, columnId: string) => {
      if (!rows) return [];

      // Create a temporary filter state without the current column's filter
      const tempFilters = { ...sortFilters };
      delete tempFilters[columnId];

      // Apply all other filters to get the relevant data
      const mutableRows = Array.from(rows);
      const filteredRows = applyFiltersAndSorting(mutableRows, tempFilters, columns || []);

      // Extract unique values from the filtered rows
      return [...new Set(filteredRows.map((r) => String(r[column.field] ?? "")))];
    },
    [rows, sortFilters, columns]
  );

  // Function to process columns that need interactive features
  const processInteractiveColumn = React.useCallback(
    (column: GridColDef) => {
      const columnId = generateColumnId(column.headerName, column.field);
      const currentFilterState = sortFilters[columnId];

      // Calculate filter data by applying all filters EXCEPT the current column's filter
      // This way, when you open a filter dropdown, you see only the values that would be visible
      // based on other active filters
      const data = calculateFilteredDataForColumn(column, columnId);

      return {
        ...column,
        sortable: false,
        disableColumnMenu: true,
        renderHeader: createInteractiveHeaderRenderer(
          column,
          data,
          currentFilterState,
          columnId,
          createFilterCallback
        ),
      };
    },
    [sortFilters, calculateFilteredDataForColumn, createFilterCallback]
  );

  // Function to process columns that need simple headers
  const processSimpleColumn = React.useCallback((column: GridColDef) => {
    return {
      ...column,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: createSimpleHeaderRenderer(column),
    };
  }, []);

  // Create custom checkbox column if checkboxSelection is enabled
  const customColumns = React.useMemo(() => {
    if (!columns) return columns;

    // Process columns to add custom headers for all columns
    const processedColumns = columns.map((column, _index) => {
      // Skip if column already has a renderHeader
      if (column.renderHeader) return column;

      // Use specific processing functions based on column properties
      const needsInteractiveFeatures = column.sortable === true || column.filterable === true;
      return needsInteractiveFeatures
        ? processInteractiveColumn(column)
        : processSimpleColumn(column);
    });

    if (!checkboxSelection) return processedColumns;

    const checkboxColumn: GridColDef = {
      field: "__checkbox__",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => {
        const { checked, indeterminate } = getHeaderState();

        return (
          <Checkbox
            color="primary"
            checked={checked}
            indeterminate={indeterminate}
            onChange={(event) => {
              handleSelectAll(event.target.checked);
            }}
          />
        );
      },
      renderCell: (params) => {
        // Determine checkbox state
        const isRowSelected = params.row.selected;
        const { allSelected, someSelected } = getChildrenState(params.row.id);

        // Row is checked if it's selected
        const isChecked = isRowSelected;

        // Row is indeterminate if it has some (but not all) descendants selected
        const isIndeterminate = someSelected && !allSelected;

        return (
          <Checkbox
            color="primary"
            checked={isChecked}
            indeterminate={isIndeterminate}
            onChange={(event) => {
              updateRowSelection(params.row.id, event.target.checked);
            }}
          />
        );
      },
    };

    return [checkboxColumn, ...processedColumns];
  }, [
    checkboxSelection,
    columns,
    processInteractiveColumn,
    processSimpleColumn,
    getHeaderState,
    handleSelectAll,
    getChildrenState,
    updateRowSelection,
  ]);

  return (
    <StyledDataGrid
      {...restProps}
      rows={processedRows}
      columns={customColumns}
      checkboxSelection={false} // Disable default checkbox since we're using custom
      localeText={{
        MuiTablePagination: {
          labelRowsPerPage: "Items per page:",
        },
      }}
    />
  );
};

// Wrapper component with forwardRef
const CustomGrid = React.forwardRef<CustomGridRef, CustomGridProps>((props, ref) => (
  <CustomGridInternal {...props} forwardedRef={ref} />
));

CustomGrid.displayName = "CustomGrid";

export default CustomGrid;
